import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, orderBy, query } from 'firebase/firestore';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import * as XLSX from 'xlsx';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import { db } from '../firebaseconfig';
import './SubmittedQuestionsPage.css';

const stringify = (value) => {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'string') return value.trim() || '-';
  return String(value);
};

const SubmittedQuestionsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [deletingId, setDeletingId] = useState('');

  const fetchSubmittedQuestions = async () => {
    setLoading(true);
    setError('');

    try {
      const firestoreQuery = query(collection(db, 'submittedQuestions'), orderBy('createdAt', 'desc'));
      const firestoreSnapshot = await getDocs(firestoreQuery);

      const firestoreRows = firestoreSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setRows(firestoreRows);
    } catch (err) {
      setError(err.message || 'Failed to fetch submitted questions from Firebase.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmittedQuestions();
  }, []);

  const visibleRows = rows.filter((entry) => {
    const isMetricRow = Boolean(entry.metricType);

    if (filter === 'metrics') return isMetricRow;
    if (filter === 'questions') return !isMetricRow;
    return true;
  });

  const getAnonymousState = (entry, name, email) => {
    return Boolean(
      entry.isAnonymous
      || /anonymous/i.test(name)
      || /not-provided/i.test(email),
    );
  };

  const handleDelete = async (entryId) => {
    if (!entryId || deletingId) return;

    const shouldDelete = window.confirm('Delete this entry from Firebase permanently?');
    if (!shouldDelete) return;

    setDeletingId(entryId);
    setError('');

    try {
      await deleteDoc(doc(db, 'submittedQuestions', entryId));
      setRows((prevRows) => prevRows.filter((row) => row.id !== entryId));
    } catch (err) {
      setError(err.message || 'Failed to delete entry from Firebase.');
    } finally {
      setDeletingId('');
    }
  };

  const handleExportToExcel = () => {
    const exportableQuestionRows = rows.filter((entry) => !entry.metricType);

    if (exportableQuestionRows.length === 0) {
      setError('No question rows available to export.');
      return;
    }

    setError('');

    const exportData = exportableQuestionRows.map((entry) => {
      const question = stringify(entry.question || entry.message || entry.text);
      const name = stringify(entry.name || entry.askedBy || entry.studentName);
      const email = stringify(entry.email || entry.replyEmail);
      const number = stringify(entry.number || entry.contact || entry.phone);
      const anonymous = getAnonymousState(entry, name, email);

      return {
        ID: entry.id || '-',
        Question: question,
        Name: anonymous ? 'Anonymous' : name,
        Email: anonymous ? '-' : email,
        Number: anonymous ? '-' : number,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SubmittedData');

    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `submitted-questions-${stamp}.xlsx`);
  };

  return (
    <main className="submitted-questions-page">
      <SEOMeta
        title="Submitted Questions | Admin | Algodoor"
        description="Admin-only view of submitted student questions."
        keywords="admin questions, submitted student questions"
        url="/submitted-questions"
        type="WebPage"
        noindex
      />

      <section className="submitted-questions-shell">
        <header className="submitted-questions-header">
          <button type="button" onClick={() => navigate('/admin')}>
            ← Back to Admin
          </button>
          <h1>Submitted Questions</h1>
          <div className="submitted-actions">
            <button type="button" className="export-btn" onClick={handleExportToExcel}>
              Export Excel
            </button>
            <button type="button" className="refresh-btn" onClick={fetchSubmittedQuestions}>
              Refresh
            </button>
          </div>
        </header>

        {!loading && !error && (
          <div className="submitted-filter-bar" role="tablist" aria-label="Submitted question filters">
            {[
              { value: 'all', label: `All (${rows.length})` },
              { value: 'questions', label: `Questions (${rows.filter((entry) => !entry.metricType).length})` },
              { value: 'metrics', label: `Metrics (${rows.filter((entry) => entry.metricType).length})` },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                className={`filter-chip${filter === item.value ? ' active' : ''}`}
                onClick={() => setFilter(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="status-text">Loading submitted questions...</p>}
        {!loading && error && <p className="status-text error">{error}</p>}

        {!loading && !error && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Number</th>
                  <th>Anonymous</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan="6" className="empty-row">No entries found for the selected filter.</td>
                  </tr>
                )}

                {visibleRows.map((entry, index) => {
                  const isMetricRow = Boolean(entry.metricType);
                  const question = stringify(entry.question || entry.message || entry.text);
                  const name = stringify(entry.name || entry.askedBy || entry.studentName);
                  const email = stringify(entry.email || entry.replyEmail);
                  const number = stringify(entry.number || entry.contact || entry.phone);
                  const anonymous = getAnonymousState(entry, name, email);

                  return (
                    <tr key={`${index}-${question}`}>
                      <td className="question-col">{isMetricRow ? `[Metric] ${question}` : question}</td>
                      <td>{anonymous ? 'Anonymous' : name}</td>
                      <td>{anonymous ? '-' : email}</td>
                      <td>{anonymous ? '-' : number}</td>
                      <td>{anonymous ? 'Yes' : 'No'}</td>
                      <td>
                        <button
                          type="button"
                          className="delete-row-btn"
                          onClick={() => handleDelete(entry.id)}
                          disabled={deletingId === entry.id}
                          title="Delete from Firebase"
                          aria-label="Delete entry"
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default SubmittedQuestionsPage;
